import { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import {
  Button,
  Popover,
  Stack,
  Divider,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { selectedStartDate, selectedEndDate } from '../../state';
import { usePersistKpiFilters } from '../../hooks/usePersistKpiFilters';

const formatDayMonth = (date: Date) => {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${mm}/${dd}`;
};

export const KpiDateRangeFilterButton = () => {
  const { persistSelectedDateRangeFilter } = usePersistKpiFilters();
  const [start, setStart] = useAtom(selectedStartDate);
  const [end, setEnd] = useAtom(selectedEndDate);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [draftStart, setDraftStart] = useState<Date | undefined>(start);
  const [draftEnd, setDraftEnd] = useState<Date | undefined>(end);

  useEffect(() => {
    setDraftStart(start);
    setDraftEnd(end);
  }, [start, end]);

  const getValidationError = () => {
    const isStartDateInvalid = draftStart && Number.isNaN(draftStart.getTime());
    const isEndDateInvalid = draftEnd && Number.isNaN(draftEnd.getTime());

    if (isStartDateInvalid && isEndDateInvalid) {
      return 'Please enter valid start and end dates';
    }
    if (isStartDateInvalid) {
      return 'Please enter a valid start date';
    }
    if (isEndDateInvalid) {
      return 'Please enter a valid end date';
    }
    if (draftStart && draftEnd && draftStart > draftEnd) {
      return 'Start date cannot be after end date';
    }
    return null;
  };

  const validationError = getValidationError();
  const hasError = Boolean(validationError);
  const canApply = !hasError;

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setDraftStart(start);
    setDraftEnd(end);
    setAnchorEl(null);
  };

  const applyDraft = () => {
    let finalStart = draftStart;
    let finalEnd = draftEnd;

    if (draftStart && draftEnd && draftEnd < draftStart) {
      finalStart = draftEnd;
      finalEnd = draftStart;
    }

    persistSelectedDateRangeFilter(finalStart, finalEnd).then(() => {
      setStart(finalStart);
      setEnd(finalEnd);
      handleClose();
    });
  };

  const displayStart = start;
  const displayEnd = end;

  const getButtonText = () => {
    if (displayStart && displayEnd) {
      return `${formatDayMonth(displayStart)} - ${formatDayMonth(displayEnd)}`;
    } else if (displayStart && !displayEnd) {
      return 'Select end date';
    } else if (!displayStart && displayEnd) {
      return `Select start date - ${formatDayMonth(displayEnd)}`;
    } else {
      return 'Select start date';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Button
        sx={{ px: 2, textTransform: 'none' }}
        variant="outlined"
        size="small"
        endIcon={<CalendarTodayIcon />}
        onClick={handleOpen}
      >
        {getButtonText()}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 320 } } }}
      >
        <Stack spacing={2}>
          <DatePicker
            label="Start date"
            value={draftStart}
            onChange={(v) => setDraftStart(v ?? undefined)}
            maxDate={draftEnd ?? undefined}
            disableFuture
            slotProps={{
              textField: ({ InputProps, ...params }) => ({
                ...params,
                id: 'kpi-tracker-start-date-filter-button',
                size: 'small',
                fullWidth: true,
                InputProps: {
                  ...InputProps,
                  endAdornment: (
                    <>
                      {draftStart ? (
                        <InputAdornment position="end">
                          <Tooltip title="Clear start date" arrow placement="bottom">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDraftStart(undefined);
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null}
                      {InputProps?.endAdornment}
                    </>
                  ),
                },
              }),
            }}
          />

          <DatePicker
            label="End date"
            value={draftEnd}
            onChange={(v) => setDraftEnd(v ?? undefined)}
            minDate={draftStart ?? undefined}
            disableFuture
            slotProps={{
              textField: ({ InputProps, ...params }) => ({
                ...params,
                id: 'kpi-tracker-end-date-filter-button',
                size: 'small',
                fullWidth: true,
                InputProps: {
                  ...InputProps,
                  endAdornment: (
                    <>
                      {draftEnd ? (
                        <InputAdornment position="end">
                          <Tooltip title="Clear end date" arrow placement="bottom">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDraftEnd(undefined);
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null}
                      {InputProps?.endAdornment}
                    </>
                  ),
                },
              }),
            }}
          />

          <Divider />

          {hasError && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {validationError}
            </Typography>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handleClose}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={applyDraft} disabled={!canApply}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </LocalizationProvider>
  );
};
